import React, { useState, useRef, useEffect, useCallback } from "react";
import ReactPlayer from "react-player";
import videoService from "../../../../services/videoService";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Divider,
  Card,
  CardContent,
  IconButton,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Chip,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import SkipPreviousIcon from "@mui/icons-material/SkipPrevious";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ShareIcon from "@mui/icons-material/Share";
import DownloadIcon from "@mui/icons-material/Download";
import TimerIcon from "@mui/icons-material/Timer";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import FlagIcon from "@mui/icons-material/Flag";

const HighlightVideo = ({ athlete }) => {
  const theme = useTheme();
  const playerRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const [muted, setMuted] = useState(false);
  const [loaded, setLoaded] = useState(0);

  const [playerReady, setPlayerReady] = useState(false);
  const [playerError, setPlayerError] = useState(null);
  const [showAddNote, setShowAddNote] = useState(false);
  const [newNote, setNewNote] = useState({
    time: 0,
    text: "",
    category: "highlight",
  });
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notes, setNotes] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [noteMenuAnchorEl, setNoteMenuAnchorEl] = useState(null);
  const [selectedNote, setSelectedNote] = useState(null);
  const [editNoteDialogOpen, setEditNoteDialogOpen] = useState(false);
  const [editedNote, setEditedNote] = useState({
    time: 0,
    text: "",
    category: "highlight",
  });
  const [addVideoDialogOpen, setAddVideoDialogOpen] = useState(false);
  const [newVideo, setNewVideo] = useState({ title: "", url: "" });

  // Format time from seconds to MM:SS
  const formatTime = (timeInSeconds) => {
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  };

  // Handle play/pause
  const handlePlayPause = () => {
    if (playing) {
      setPlaying(false);
    } else {
      setPlaying(true);
    }
  };

  // Check if URL is from Hudl
  const isHudlVideo = (url) => {
    return url && url.includes("hudl.com");
  };

  // Simple handlers for ReactPlayer v2
  const handleReady = () => {
    setPlayerReady(true);
  };

  // Handle duration loaded
  const handleDuration = (duration) => {
    setDuration(duration);
    // Ensure player is ready when duration is available
    setPlayerReady(true);
  };

  const handleError = (error) => {
    setPlayerError(`Error: ${error}`);
    setPlayerReady(false);
    setPlaying(false);
  };

  // Handle seeking to specific time
  const handleSeekTo = (e) => {
    if (!playerReady || !duration) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const seekTime = ((e.clientX - rect.left) / rect.width) * duration;

    if (playerRef.current && playerRef.current.seekTo) {
      playerRef.current.seekTo(seekTime, "seconds");
      setCurrentTime(seekTime);
    }
  };

  // Handle volume change
  const handleVolumeToggle = () => {
    setMuted(!muted);
  };

  // Handle skip to previous/next note
  const handleSkipToPrevNote = () => {
    if (!playerReady) return;

    const sortedNotes = [...notes].sort((a, b) => a.time - b.time);
    const prevNote = sortedNotes
      .filter((note) => note.time < currentTime)
      .pop();

    if (prevNote && playerRef.current && playerRef.current.seekTo) {
      playerRef.current.seekTo(prevNote.time);
      setCurrentTime(prevNote.time);
    }
  };

  const handleSkipToNextNote = () => {
    if (!playerReady) return;

    const sortedNotes = [...notes].sort((a, b) => a.time - b.time);
    const nextNote = sortedNotes.find((note) => note.time > currentTime);

    if (nextNote && playerRef.current && playerRef.current.seekTo) {
      playerRef.current.seekTo(nextNote.time);
      setCurrentTime(nextNote.time);
    }
  };

  // Handle add note
  const handleAddNoteClick = () => {
    setNewNote({ ...newNote, time: currentTime });
    setShowAddNote(true);
    if (playing) {
      handlePlayPause();
    }
  };

  const handleAddNote = async () => {
    if (newNote.text.trim() && selectedVideo) {
      try {
        const noteData = {
          time: newNote.time,
          text: newNote.text,
          category: newNote.category,
        };

        const response = await videoService.addNote(
          selectedVideo._id,
          noteData
        );
        if (response.success) {
          // Update the selected video with new notes
          setSelectedVideo(response.data);
          setNotes(response.data.notes || []);
          setNewNote({ time: 0, text: "", category: "highlight" });
          setShowAddNote(false);
        }
      } catch (error) {
        console.error("Error adding note:", error);
        setError("Failed to add note");
      }
    }
  };

  // Handle click on note to jump to that time
  const handleNoteClick = (time) => {
    if (!playerReady) return;

    if (playerRef.current && playerRef.current.seekTo) {
      playerRef.current.seekTo(time);
      setCurrentTime(time);
      if (!playing) {
        setPlaying(true);
      }
    }
  };

  const handleNoteMenuOpen = (event, note) => {
    event.stopPropagation();
    setSelectedNote(note);
    setNoteMenuAnchorEl(event.currentTarget);
  };

  const handleNoteMenuClose = () => {
    setNoteMenuAnchorEl(null);
  };

  // Handle edit note
  const handleEditNoteClick = () => {
    setEditedNote({ ...selectedNote });
    setEditNoteDialogOpen(true);
    handleNoteMenuClose();
  };

  const handleEditNoteSave = async () => {
    if (editedNote.text.trim() && selectedVideo && selectedNote) {
      try {
        const response = await videoService.updateNote(
          selectedVideo._id,
          selectedNote._id,
          editedNote
        );
        if (response.success) {
          setSelectedVideo(response.data);
          setNotes(response.data.notes || []);
          setEditNoteDialogOpen(false);
        }
      } catch (error) {
        console.error("Error updating note:", error);
        setError("Failed to update note");
      }
    }
  };

  // Handle delete note
  const handleDeleteNote = async () => {
    if (selectedVideo && selectedNote) {
      try {
        await videoService.deleteNote(selectedVideo._id, selectedNote._id);
        // Refresh the video to get updated notes
        const response = await videoService.getVideo(selectedVideo._id);
        if (response.success) {
          setSelectedVideo(response.data);
          setNotes(response.data.notes || []);
        }
        handleNoteMenuClose();
      } catch (error) {
        console.error("Error deleting note:", error);
        setError("Failed to delete note");
      }
    }
  };

  // Handle video selection
  const handleVideoSelect = (video) => {
    // Stop current video first
    setPlaying(false);

    // Reset player state
    setCurrentTime(0);
    setDuration(0);
    setLoaded(0);
    setPlayerReady(false);
    setPlayerError(null); // Clear any previous errors

    // Set new video and notes
    setSelectedVideo(video);
    setNotes(video.notes || []);
  };

  // Handle opening add video dialog
  const handleAddVideoClick = () => {
    setNewVideo({ title: "", url: "" });
    setAddVideoDialogOpen(true);
  };

  // Handle closing add video dialog
  const handleAddVideoClose = () => {
    setAddVideoDialogOpen(false);
  };

  // Handle new video input change
  const handleNewVideoChange = (e) => {
    const { name, value } = e.target;
    setNewVideo((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle adding a new video
  const handleAddVideo = async () => {
    // Validate inputs
    if (!newVideo.title.trim() || !newVideo.url.trim()) {
      return; // Don't add if empty
    }

    try {
      const videoData = {
        title: newVideo.title.trim(),
        url: newVideo.url.trim(),
        prospect: athlete.id || athlete._id,
        category: "highlight",
        platform: videoService.detectPlatform(newVideo.url.trim()),
      };

      const response = await videoService.createVideo(videoData);

      if (response.success) {
        // Refresh videos list
        await fetchVideos();

        // Select the new video
        setSelectedVideo(response.data);
        setNotes(response.data.notes || []);

        // Close dialog and reset form
        setAddVideoDialogOpen(false);
        setNewVideo({ title: "", url: "" });
      }
    } catch (error) {
      console.error("Error adding video:", error);
      setError("Failed to add video");
    }
  };

  // Fetch videos from API
  const fetchVideos = useCallback(async () => {
    if (!athlete.id && !athlete._id) return;

    try {
      setLoading(true);
      setError(null);
      const response = await videoService.getProspectVideos(
        athlete.id || athlete._id
      );

      if (response.success) {
        setVideos(response.data);
        // Select first video if none selected and no videos exist yet
        if (response.data.length > 0 && !selectedVideo) {
          setSelectedVideo(response.data[0]);
          setNotes(response.data[0].notes || []);
        }
      }
    } catch (error) {
      console.error("Error fetching videos:", error);
      // In development mode, show error. In sandbox mode, the API will handle test data
      setError("Failed to load videos");
    } finally {
      setLoading(false);
    }
  }, [athlete.id, athlete._id, selectedVideo]);

  // Get note category color
  const getNoteColor = (category) => {
    return videoService.getNoteColor(category);
  };

  // Fetch videos on component mount
  useEffect(() => {
    fetchVideos();
  }, [athlete.id, athlete._id, fetchVideos]);

  // Update video when selected video changes
  useEffect(() => {
    setPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    setLoaded(0);
    setPlayerReady(false);
    setPlayerError(null); // Clear errors when switching videos
  }, [selectedVideo]);

  return (
    <Paper elevation={0} variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
      <Typography
        variant="h6"
        gutterBottom
        sx={{ fontWeight: "bold", display: "flex", alignItems: "center" }}
      >
        <VideoLibraryIcon sx={{ mr: 1 }} color="primary" />
        Highlight Videos
        <Box sx={{ ml: "auto", display: "flex", alignItems: "center" }}>
          <Button
            startIcon={<AddIcon />}
            size="small"
            onClick={handleAddVideoClick}
            sx={{ mr: 1 }}
          >
            Add Video
          </Button>
        </Box>
      </Typography>
      <Divider sx={{ mb: 3 }} />

      {/* Error Display */}
      {error && (
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="body2"
            color="error"
            sx={{ p: 2, bgcolor: "error.light", borderRadius: 1 }}
          >
            {error}
          </Typography>
        </Box>
      )}

      {/* Video List Section - Display all videos with newest at top */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          All Highlight Videos {loading && "(Loading...)"}
        </Typography>
        <Grid container spacing={2}>
          {videos.map((video) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={video._id || video.id}>
              <Card
                variant="outlined"
                sx={{
                  cursor: "pointer",
                  bgcolor:
                    selectedVideo?._id === video._id ||
                    selectedVideo?.id === video.id
                      ? "action.selected"
                      : "background.paper",
                  transition: "all 0.2s",
                  "&:hover": { boxShadow: 2 },
                }}
              >
                <Box
                  sx={{
                    height: 120,
                    bgcolor: "grey.800",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative",
                    overflow: "hidden",
                    cursor: "pointer",
                  }}
                  onClick={() => handleVideoSelect(video)}
                >
                  {ReactPlayer.canPlay(video.url) ? (
                    <ReactPlayer
                      url={video.url}
                      width="100%"
                      height="100%"
                      light={true}
                      playing={false}
                      controls={false}
                      playIcon={
                        <PlayArrowIcon
                          sx={{
                            fontSize: 48,
                            color: "white",
                            backgroundColor: "rgba(0,0,0,0.7)",
                            borderRadius: "50%",
                            p: 1,
                            border: "2px solid white",
                          }}
                        />
                      }
                      config={{
                        youtube: {
                          playerVars: {
                            showinfo: 0,
                            controls: 0,
                            modestbranding: 1,
                          },
                        },
                        vimeo: {
                          playerOptions: {
                            controls: false,
                            background: true,
                          },
                        },
                      }}
                      onClickPreview={() => handleVideoSelect(video)}
                      previewTabIndex={0}
                    />
                  ) : video.thumbnail ? (
                    <>
                      <img
                        src={video.thumbnail}
                        alt={video.title}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                      <PlayArrowIcon
                        sx={{
                          position: "absolute",
                          fontSize: 48,
                          color: "white",
                          backgroundColor: "rgba(0,0,0,0.7)",
                          borderRadius: "50%",
                          p: 1,
                          border: "2px solid white",
                        }}
                      />
                    </>
                  ) : (
                    <>
                      <VideoLibraryIcon
                        sx={{ fontSize: 40, color: "grey.500" }}
                      />
                      <PlayArrowIcon
                        sx={{
                          position: "absolute",
                          fontSize: 32,
                          color: "grey.400",
                        }}
                      />
                    </>
                  )}
                </Box>
                <CardContent
                  sx={{ py: 1, cursor: "pointer" }}
                  onClick={() => handleVideoSelect(video)}
                >
                  <Typography variant="body2" noWrap title={video.title}>
                    {video.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {video.uploadDate
                      ? new Date(video.uploadDate).toLocaleDateString()
                      : "No date"}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      <Grid container spacing={3}>
        {/* Video Player */}
        <Grid item xs={12} md={8}>
          <Box
            sx={{
              position: "relative",
              width: "100%",
              bgcolor: "black",
              borderRadius: 1,
              overflow: "hidden",
            }}
          >
            {/* Video */}
            {selectedVideo ? (
              <>
                {playerError ? (
                  <Box
                    sx={{
                      height: 400,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: "grey.900",
                      color: "error.main",
                      flexDirection: "column",
                      p: 3,
                      textAlign: "center",
                    }}
                  >
                    <Typography variant="h6" color="error" gutterBottom>
                      Video Error
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 2 }}
                    >
                      {playerError}
                    </Typography>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={() => {
                        setPlayerError(null);
                        setPlayerReady(false);
                      }}
                    >
                      Retry
                    </Button>
                  </Box>
                ) : isHudlVideo(selectedVideo.url) ? (
                  // Hudl Video - Simple Open Button
                  <Box
                    sx={{
                      height: 400,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: "grey.900",
                      flexDirection: "column",
                    }}
                  >
                    <VideoLibraryIcon
                      sx={{ fontSize: 80, mb: 3, color: "primary.main" }}
                    />
                    <Button
                      variant="contained"
                      color="primary"
                      size="large"
                      onClick={() => window.open(selectedVideo.url, "_blank")}
                      startIcon={<VideoLibraryIcon />}
                    >
                      Open in Hudl
                    </Button>
                  </Box>
                ) : (
                  <ReactPlayer
                    ref={playerRef}
                    url={selectedVideo.url}
                    width="100%"
                    height="400px"
                    playing={playing}
                    controls={false}
                    muted={muted}
                    onReady={handleReady}
                    onError={handleError}
                    onPlay={() => setPlaying(true)}
                    onPause={() => setPlaying(false)}
                    onProgress={(progress) => {
                      setCurrentTime(progress.playedSeconds);
                      setLoaded(progress.loaded);
                    }}
                    onDuration={handleDuration}
                    config={{
                      youtube: {
                        playerVars: {
                          showinfo: 0,
                          controls: 0,
                          modestbranding: 1,
                          rel: 0,
                        },
                      },
                      vimeo: {
                        playerOptions: {
                          controls: false,
                          background: false,
                        },
                      },
                      file: {
                        attributes: {
                          controlsList: "nodownload",
                        },
                      },
                    }}
                    style={{ backgroundColor: "black" }}
                  />
                )}
              </>
            ) : (
              <Box
                sx={{
                  height: 300,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "grey.900",
                  color: "grey.500",
                  flexDirection: "column",
                }}
              >
                <VideoLibraryIcon sx={{ fontSize: 60, mb: 2 }} />
                <Typography variant="body1">No videos available</Typography>
                <Button
                  variant="outlined"
                  color="primary"
                  sx={{ mt: 2 }}
                  onClick={handleAddVideoClick}
                  startIcon={<AddIcon />}
                >
                  Add First Video
                </Button>
              </Box>
            )}

            {/* Video Controls */}
            <Box
              sx={{
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                bgcolor: "rgba(0, 0, 0, 0.8)",
                p: 2,
                display: "flex",
                flexDirection: "column",
                zIndex: 10,
                backdropFilter: "blur(4px)",
              }}
            >
              {/* Progress Bar */}
              <Box
                sx={{
                  width: "100%",
                  height: 5,
                  bgcolor: "rgba(255, 255, 255, 0.3)",
                  cursor: "pointer",
                  mb: 1,
                  borderRadius: 5,
                  position: "relative",
                }}
                onClick={handleSeekTo}
              >
                {/* Loaded progress (buffer) */}
                <Box
                  sx={{
                    width: `${loaded * 100}%`,
                    height: "100%",
                    bgcolor: "rgba(255, 255, 255, 0.5)",
                    borderRadius: 5,
                    position: "absolute",
                  }}
                />

                {/* Played progress */}
                <Box
                  sx={{
                    width: `${
                      duration > 0 ? (currentTime / duration) * 100 : 0
                    }%`,
                    height: "100%",
                    bgcolor: theme.palette.primary.main,
                    borderRadius: 5,
                    position: "relative",
                    zIndex: 1,
                  }}
                />

                {/* Note Markers */}
                {notes.map((note) => (
                  <Box
                    key={note._id || note.id}
                    sx={{
                      position: "absolute",
                      left: `${
                        duration > 0 ? (note.time / duration) * 100 : 0
                      }%`,
                      top: -3,
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      bgcolor: getNoteColor(note.category),
                      transform: "translateX(-50%)",
                      border: "1px solid white",
                      zIndex: 2,
                      cursor: "pointer",
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleNoteClick(note.time);
                    }}
                  />
                ))}
              </Box>

              {/* Controls */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <IconButton
                    size="medium"
                    onClick={handleSkipToPrevNote}
                    sx={{
                      color: "white",
                      bgcolor: "rgba(255,255,255,0.1)",
                      "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
                    }}
                    disabled={!playerReady || notes.length === 0}
                  >
                    <SkipPreviousIcon />
                  </IconButton>

                  <IconButton
                    size="large"
                    onClick={handlePlayPause}
                    sx={{
                      color: "white",
                      bgcolor: "rgba(255,255,255,0.1)",
                      "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
                      mx: 1,
                    }}
                    disabled={!selectedVideo}
                  >
                    {playing ? <PauseIcon /> : <PlayArrowIcon />}
                  </IconButton>

                  <IconButton
                    size="medium"
                    onClick={handleSkipToNextNote}
                    sx={{
                      color: "white",
                      bgcolor: "rgba(255,255,255,0.1)",
                      "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
                    }}
                    disabled={!playerReady || notes.length === 0}
                  >
                    <SkipNextIcon />
                  </IconButton>

                  <Typography variant="caption" sx={{ color: "white", mx: 1 }}>
                    {formatTime(currentTime)} / {formatTime(duration || 0)}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <IconButton
                    size="medium"
                    onClick={handleAddNoteClick}
                    sx={{
                      color: "white",
                      bgcolor: "rgba(255,255,255,0.1)",
                      "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
                      mr: 1,
                    }}
                    disabled={!playerReady}
                  >
                    <AddIcon />
                  </IconButton>

                  <IconButton
                    size="medium"
                    onClick={handleVolumeToggle}
                    sx={{
                      color: "white",
                      bgcolor: "rgba(255,255,255,0.1)",
                      "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
                    }}
                    disabled={!selectedVideo}
                  >
                    {muted ? <VolumeOffIcon /> : <VolumeUpIcon />}
                  </IconButton>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Add Note Form */}
          {showAddNote && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                bgcolor: "background.default",
                borderRadius: 1,
              }}
            >
              <Typography variant="subtitle2" gutterBottom>
                Add Note at {formatTime(newNote.time)}
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={2}
                placeholder="Enter your note here..."
                value={newNote.text}
                onChange={(e) =>
                  setNewNote({ ...newNote, text: e.target.value })
                }
                sx={{ mb: 2 }}
              />
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <Typography variant="body2" sx={{ mr: 2 }}>
                  Category:
                </Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                  {["highlight", "technique", "improvement", "concern"].map(
                    (category) => (
                      <Chip
                        key={category}
                        label={
                          category.charAt(0).toUpperCase() + category.slice(1)
                        }
                        onClick={() => setNewNote({ ...newNote, category })}
                        color={
                          newNote.category === category ? "primary" : "default"
                        }
                        variant={
                          newNote.category === category ? "filled" : "outlined"
                        }
                        size="small"
                      />
                    )
                  )}
                </Box>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                <Button
                  variant="outlined"
                  onClick={() => setShowAddNote(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleAddNote}
                  disabled={!newNote.text.trim()}
                >
                  Add Note
                </Button>
              </Box>
            </Box>
          )}

          {/* Video Description */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              {selectedVideo?.title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {selectedVideo?.description}
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                mt: 1,
                flexWrap: "wrap",
                gap: 1,
              }}
            >
              {selectedVideo?.uploadDate && (
                <Chip
                  label={`Uploaded: ${new Date(
                    selectedVideo.uploadDate
                  ).toLocaleDateString()}`}
                  size="small"
                  variant="outlined"
                />
              )}
              {duration > 0 && (
                <Chip
                  label={`Duration: ${formatTime(duration)}`}
                  size="small"
                  variant="outlined"
                />
              )}
              {selectedVideo?.source && (
                <Chip
                  label={`Source: ${selectedVideo.source}`}
                  size="small"
                  variant="outlined"
                />
              )}
            </Box>
          </Box>
        </Grid>

        {/* Notes Panel */}
        <Grid item xs={12} md={4}>
          <Card
            variant="outlined"
            sx={{ height: "100%", display: "flex", flexDirection: "column" }}
          >
            <CardContent sx={{ p: 2, pb: 0, flexGrow: 0 }}>
              <Typography
                variant="subtitle1"
                gutterBottom
                sx={{
                  fontWeight: "medium",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <TimerIcon sx={{ mr: 1 }} fontSize="small" />
                Timestamp Notes
              </Typography>
            </CardContent>

            <Divider />

            <Box sx={{ flexGrow: 1, overflow: "auto", maxHeight: 400 }}>
              {notes.length > 0 ? (
                <List dense>
                  {[...notes]
                    .sort((a, b) => a.time - b.time)
                    .map((note) => (
                      <ListItem
                        key={note._id || note.id}
                        button
                        onClick={() => handleNoteClick(note.time)}
                        sx={{
                          borderLeft: `4px solid ${getNoteColor(
                            note.category
                          )}`,
                          bgcolor:
                            currentTime >= note.time &&
                            currentTime < note.time + 5
                              ? "action.selected"
                              : "inherit",
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          <Typography variant="body2" color="text.secondary">
                            {formatTime(note.time)}
                          </Typography>
                        </ListItemIcon>
                        <ListItemText
                          primary={note.text}
                          primaryTypographyProps={{ variant: "body2" }}
                        />
                        <ListItemSecondaryAction>
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={(e) => handleNoteMenuOpen(e, note)}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                </List>
              ) : (
                <Box sx={{ p: 3, textAlign: "center" }}>
                  <Typography variant="body2" color="text.secondary">
                    No notes for this video yet.
                  </Typography>
                  <Button
                    startIcon={<AddIcon />}
                    onClick={handleAddNoteClick}
                    sx={{ mt: 1 }}
                  >
                    Add First Note
                  </Button>
                </Box>
              )}
            </Box>

            <Divider />

            <Box sx={{ p: 2, bgcolor: "background.default" }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Note Categories:
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                <Chip
                  icon={<FlagIcon sx={{ color: theme.palette.success.main }} />}
                  label="Highlight"
                  size="small"
                  variant="outlined"
                />
                <Chip
                  icon={<FlagIcon sx={{ color: theme.palette.info.main }} />}
                  label="Technique"
                  size="small"
                  variant="outlined"
                />
                <Chip
                  icon={<FlagIcon sx={{ color: theme.palette.warning.main }} />}
                  label="Improvement"
                  size="small"
                  variant="outlined"
                />
                <Chip
                  icon={<FlagIcon sx={{ color: theme.palette.error.main }} />}
                  label="Concern"
                  size="small"
                  variant="outlined"
                />
              </Box>
            </Box>
          </Card>
        </Grid>
      </Grid>

      {/* Note Menu */}
      <Menu
        anchorEl={noteMenuAnchorEl}
        open={Boolean(noteMenuAnchorEl)}
        onClose={handleNoteMenuClose}
      >
        <MenuItem onClick={handleEditNoteClick}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Edit Note" />
        </MenuItem>
        <MenuItem onClick={handleDeleteNote}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Delete Note" />
        </MenuItem>
      </Menu>

      {/* Edit Note Dialog */}
      <Dialog
        open={editNoteDialogOpen}
        onClose={() => setEditNoteDialogOpen(false)}
      >
        <DialogTitle>Edit Note at {formatTime(editedNote.time)}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={3}
            value={editedNote.text}
            onChange={(e) =>
              setEditedNote({ ...editedNote, text: e.target.value })
            }
            sx={{ mt: 1 }}
          />
          <Box sx={{ display: "flex", alignItems: "center", mt: 2 }}>
            <Typography variant="body2" sx={{ mr: 2 }}>
              Category:
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              {["highlight", "technique", "improvement", "concern"].map(
                (category) => (
                  <Chip
                    key={category}
                    label={category.charAt(0).toUpperCase() + category.slice(1)}
                    onClick={() => setEditedNote({ ...editedNote, category })}
                    color={
                      editedNote.category === category ? "primary" : "default"
                    }
                    variant={
                      editedNote.category === category ? "filled" : "outlined"
                    }
                    size="small"
                  />
                )
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditNoteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleEditNoteSave}
            variant="contained"
            disabled={!editedNote.text.trim()}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Video Dialog */}
      <Dialog open={addVideoDialogOpen} onClose={handleAddVideoClose}>
        <DialogTitle>Add New Highlight Video</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Video Title"
            name="title"
            value={newVideo.title}
            onChange={handleNewVideoChange}
            placeholder="e.g. Senior Year Highlights"
            sx={{ mt: 1, mb: 2 }}
          />
          <TextField
            fullWidth
            label="Video URL"
            name="url"
            value={newVideo.url}
            onChange={handleNewVideoChange}
            placeholder="https://www.youtube.com/watch?v=dQw4w9WgXcQ"
            helperText="Supports YouTube, Vimeo, MP4/WebM files, Wistia, DailyMotion. Hudl videos will open in a new tab."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAddVideoClose}>Cancel</Button>
          <Button
            onClick={handleAddVideo}
            variant="contained"
            disabled={!newVideo.title.trim() || !newVideo.url.trim()}
          >
            Add Video
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default HighlightVideo;
